import React, { useState } from 'react';
import applicationService from '../services/applicationService';

const TPCESDSection = ({ application, userRole, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  const isCandidate = userRole === 'CANDIDATE';
  const isHrAdmin = ['HR', 'ADMIN', 'CHAIRMAN'].includes(userRole);
  const isTPCStage = application?.currentStage === 'TPC_PRACTISING';
  const isESDStage = application?.currentStage === 'ESD_APPROVAL';

  if (!isTPCStage && !isESDStage) {
    return null;
  }

  const currentData = isTPCStage ? application?.tpcPractising : application?.esdApproval;
  const sectionTitle = isTPCStage ? 'TPC - Practising' : 'ESD Approval';
  const applyButtonLabel = isTPCStage ? 'Mark TPC as Applied' : 'Mark ESD as Applied';
  const uploadButtonLabel = isTPCStage ? 'Upload Practising Certificate' : 'Upload ESD Certificate';
  const viewLabel = isTPCStage ? 'View Practising Certificate' : 'View ESD Certificate';

  const getDocumentViewUrl = (url, fileName = '') => {
    if (!url) return url;

    const lowerName = (fileName || '').toLowerCase();
    const lowerUrl = String(url).toLowerCase();
    const isPreviewableDoc =
      lowerName.endsWith('.pdf') ||
      lowerName.endsWith('.doc') ||
      lowerName.endsWith('.docx') ||
      lowerUrl.includes('.pdf') ||
      lowerUrl.includes('.doc') ||
      lowerUrl.includes('.docx');

    if (isPreviewableDoc) {
      return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(url)}`;
    }

    return url;
  };

  const withAction = async (fn) => {
    setBusy(true);
    try {
      await fn();
      if (onRefresh) onRefresh();
    } catch (error) {
      alert(error.response?.data?.error || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleApply = () => {
    withAction(async () => {
      if (isTPCStage) {
        await applicationService.applyForTPCPractising(application._id);
        alert('TPC marked as applied. You can now upload the certificate.');
      } else {
        await applicationService.applyForESDApproval(application._id);
        alert('ESD marked as applied. You can now upload the certificate.');
      }
    });
  };

  const handleUpload = () => {
    if (!uploadFile) {
      alert('Please select a file first');
      return;
    }

    withAction(async () => {
      if (isTPCStage) {
        await applicationService.uploadTPCPractisingCertificate(application._id, uploadFile);
        alert('Practising certificate uploaded. Stage moved to ESD Approval.');
      } else {
        await applicationService.uploadESDCertificate(application._id, uploadFile);
        alert('ESD certificate uploaded. Stage moved to Visa Process.');
      }

      setUploadFile(null);
      const input = document.getElementById('tpc-esd-upload-input');
      if (input) input.value = '';
    });
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <h3 className="m-0 mb-3 text-base font-semibold text-primary pb-1 border-b border-accent-green">{sectionTitle}</h3>

      <div className="flex flex-col gap-3">
        {currentData?.certificateFileUrl ? (
          <div className="text-sm text-[#155724] bg-[#d4edda] border border-[#c3e6cb] rounded p-2">
            {isTPCStage ? 'TPC cleared' : 'ESD cleared'}
          </div>
        ) : currentData?.candidateAppliedAt ? (
          <div className="text-sm text-[#155724] bg-[#d4edda] border border-[#c3e6cb] rounded p-2">
            {isTPCStage
              ? 'Application is applied for TPC certificate.'
              : 'Application is applied for ESD certificate.'}
          </div>
        ) : (
          <div className="text-sm text-[#6c757d] bg-[#f8f9fa] border border-[#dee2e6] rounded p-2">
            {isCandidate ? 'Waiting for admin to mark as applied.' : 'Application has not been marked as applied yet.'}
          </div>
        )}

        {isHrAdmin && !currentData?.candidateAppliedAt && (
          <button
            type="button"
            disabled={busy}
            onClick={handleApply}
            className="px-4 py-2.5 border-none rounded-md text-sm font-semibold cursor-pointer transition-all duration-200 bg-[#007bff] text-white hover:bg-[#0056b3] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {applyButtonLabel}
          </button>
        )}

        {currentData?.certificateFileUrl && (
          <div className="flex flex-col gap-1">
            <a
              href={getDocumentViewUrl(currentData.certificateFileUrl, currentData.certificateFileName)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white max-w-fit"
            >
              {viewLabel}
            </a>
            <span className="text-xs text-[#6c757d]">{currentData.certificateFileName || 'certificate.pdf'}</span>
            {isCandidate && (
              <span className="text-xs text-[#6c757d]">Uploaded by admin.</span>
            )}
          </div>
        )}

        {isHrAdmin && currentData?.candidateAppliedAt && !currentData?.certificateFileUrl && (
          <div className="bg-[#f8f9fa] border border-[#dee2e6] rounded p-3">
            <label htmlFor="tpc-esd-upload-input" className="block text-sm font-medium text-[#495057] mb-2">
              Upload Certificate
            </label>
            <input
              id="tpc-esd-upload-input"
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
              disabled={busy}
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="w-full p-2.5 border border-[#ced4da] rounded text-[0.95rem] bg-white mb-2"
            />
            <button
              type="button"
              disabled={busy || !uploadFile}
              onClick={handleUpload}
              className="px-4 py-2.5 border-none rounded-md text-sm font-semibold cursor-pointer transition-all duration-200 bg-accent-green text-white hover:bg-[#5a6b4d] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? 'Processing...' : uploadButtonLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TPCESDSection;

